import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';
import Button from '../ui/Button';
import type { PendingActionItem } from '../../api/pendingActions';
import { sourceMeta, relativeTime, leaveSubtitle } from './sourceMeta';
import { completeActionLabel, isDispatchable } from './dispatch';

export type RowDecision = 'positive' | 'negative' | 'open' | `rate-${number}` | null;

/** The one-tap eNPS answers offered inline on the Survey row. */
const SURVEY_QUICK_RATINGS = [0, 5, 10] as const;

interface PendingActionRowProps {
  item: PendingActionItem;
  locale: string;
  /** The in-flight decision on THIS row (disables its buttons). */
  busy: RowDecision;
  onDecision: (item: PendingActionItem, positive: boolean) => void;
  onOpen: (item: PendingActionItem) => void;
  onDetails: (item: PendingActionItem) => void;
  onQuickRate: (item: PendingActionItem, rating: number) => void;
  /** True when Details navigates to the source's page; false = opens the fallback dialog. */
  detailsNavigates?: boolean;
}

/**
 * One inbox row: source icon, what's pending, who it's from, and the quick
 * action(s) inline. "Details" is always available; it is never the only path.
 */
const PendingActionRow: React.FC<PendingActionRowProps> = ({ item, locale, busy, onDecision, onOpen, onDetails, onQuickRate, detailsNavigates = false }) => {
  const { t } = useTranslation('requests');
  const meta = sourceMeta(item.source);
  const SourceIcon = meta.icon;
  const disabled = busy !== null;

  // System-generated title/subtitle localize on the client from the item's
  // structured Meta; genuine user data (leave TYPE, requester name) stays as the
  // server sent it. The Survey subtitle is the fixed eNPS question.
  const title = item.source === 'Survey' ? t('Monthly pulse') : item.title;
  const subtitle =
    item.source === 'Survey'
      ? t('How likely are you to recommend this company as a great place to work?')
      : item.source === 'Leave'
        ? leaveSubtitle(item.meta, locale, t) ?? item.subtitle
        : item.subtitle;

  const actions: React.ReactNode = (() => {
    // Survey answers inline: the monthly pulse is a single 0–10 question, so
    // three anchor scores beat a detour through the survey page. The full
    // page (with feedback text) stays reachable via Details.
    if (item.source === 'Survey') {
      return (
        <div className="flex items-center gap-1" role="group" aria-label={t('Rate from 0 to 10')}>
          {SURVEY_QUICK_RATINGS.map((rating) => (
            <Button
              key={rating}
              variant="outline"
              size="xs"
              className="min-w-9 tabular-nums"
              disabled={disabled}
              loading={busy === `rate-${rating}`}
              onClick={() => onQuickRate(item, rating)}
              aria-label={t('Rate {{n}} out of 10', { n: rating })}
              data-testid={`pa-survey-rate-${rating}`}
            >
              {rating}
            </Button>
          ))}
        </div>
      );
    }
    // A decision button is only honest when `dispatchDecision` can carry it. The
    // server pairs each source with an actionStyle, but that pairing is across a
    // wire boundary: fall back to Open rather than paint an Approve that can only
    // return NotDispatchable.
    const style = isDispatchable(item.source) ? item.actionStyle : 'OpenOnly';
    switch (style) {
      case 'Decision2':
        return (
          <>
            <Button
              variant="outline"
              size="xs"
              disabled={disabled}
              loading={busy === 'negative'}
              onClick={() => onDecision(item, false)}
              aria-label={`${t('Reject')}: ${title}`}
              data-testid="pa-reject"
            >
              {t('Reject')}
            </Button>
            <Button
              variant="success"
              size="xs"
              disabled={disabled}
              loading={busy === 'positive'}
              onClick={() => onDecision(item, true)}
              aria-label={`${t('Approve')}: ${title}`}
              data-testid="pa-approve"
            >
              {t('Approve')}
            </Button>
          </>
        );
      case 'Accept2':
        return (
          <>
            <Button
              variant="outline"
              size="xs"
              disabled={disabled}
              loading={busy === 'negative'}
              onClick={() => onDecision(item, false)}
              aria-label={`${t('Decline')}: ${title}`}
              data-testid="pa-decline"
            >
              {t('Decline')}
            </Button>
            <Button
              variant="success"
              size="xs"
              disabled={disabled}
              loading={busy === 'positive'}
              onClick={() => onDecision(item, true)}
              aria-label={`${t('Accept')}: ${title}`}
              data-testid="pa-accept"
            >
              {t('Accept')}
            </Button>
          </>
        );
      case 'Complete1':
        return (
          <Button
            variant="success"
            size="xs"
            disabled={disabled}
            loading={busy === 'positive'}
            onClick={() => onDecision(item, true)}
            aria-label={`${t(completeActionLabel(item.source))}: ${title}`}
            data-testid="pa-complete"
          >
            {t(completeActionLabel(item.source))}
          </Button>
        );
      case 'OpenOnly':
      default:
        return (
          <Button
            variant="default"
            size="xs"
            disabled={disabled}
            onClick={() => onOpen(item)}
            aria-label={`${t('Open')}: ${title}`}
            data-testid="pa-open"
          >
            {t('Open')}
          </Button>
        );
    }
  })();

  return (
    <div
      className={`flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3 sm:flex-nowrap ${
        item.blocking ? 'bg-amber-500/5' : ''
      }`}
      data-testid={`pa-row-${item.source}`}
    >
      {/* Source icon — one quiet treatment; amber only when the item blocks the user. */}
      <div
        className={`flex h-9 w-9 flex-none items-center justify-center rounded-full ${
          item.blocking ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400' : 'bg-muted text-muted-foreground'
        }`}
        aria-hidden="true"
      >
        {item.blocking ? <AlertCircle size={17} /> : <SourceIcon size={17} />}
      </div>

      {/* What + who + when */}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2">
          <p className="truncate text-sm font-medium text-foreground">{title}</p>
          {item.blocking && (
            <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-semibold text-amber-700 dark:text-amber-400">
              {t('Required')}
            </span>
          )}
        </div>
        <p className="truncate text-xs text-muted-foreground">
          {[item.actorName, subtitle, relativeTime(item.submittedOn, locale, t)]
            .filter(Boolean)
            .join(' · ')}
        </p>
      </div>

      {/* Quick actions + the always-available deep dive */}
      <div className="flex flex-none items-center gap-2 ms-auto">
        {actions}
        <Button
          variant="ghost"
          size="xs"
          disabled={disabled}
          onClick={() => onDetails(item)}
          aria-haspopup={detailsNavigates ? undefined : 'dialog'}
          aria-label={`${t('Details')}: ${title}`}
          data-testid="pa-details"
        >
          {t('Details')}
        </Button>
      </div>
    </div>
  );
};

export default PendingActionRow;
